/**
 * The credits usage-bar level, keyed to the fraction of the cap USED: <50% green, 50–<85% orange,
 * ≥85% red. Shared 1:1 with the native apps (`creditsUsageLevel` in iOS `Components.swift` and
 * Android `CreditsUsage.kt`), thresholds and boundary equality included — a bar that is red in the
 * drawer must be red on the account screen, on every platform.
 */
export type CreditsUsageLevel = 'green' | 'orange' | 'red';

export function creditsUsageLevel(fraction: number): CreditsUsageLevel {
  if (fraction >= 0.85) return 'red';
  if (fraction >= 0.5) return 'orange';
  return 'green';
}

/**
 * The same ladder, spoken as the DS Progress Bar's `progress` level — which is what every credits
 * meter renders through now. The three steps resolve to exactly the colours the hand-rolled bars
 * used to paint (`--ds-fill-accent-brand-default` #A4FF70, `--ds-text-pending` #FF773A,
 * `--ds-fill-destructive-default` #DF3336, the last two being what `--ds-peach` and `--ds-red-500`
 * alias to), so moving onto the primitive changed the vocabulary and not the design.
 *
 * Kept as a mapping over `creditsUsageLevel` rather than folded into it: green/orange/red are the
 * native apps' own names, and the boundary test that pins cross-platform parity is written against
 * them.
 */
export function creditsProgress(fraction: number): 'success' | 'pending' | 'destructive' {
  switch (creditsUsageLevel(fraction)) {
    case 'red':
      return 'destructive';
    case 'orange':
      return 'pending';
    default:
      return 'success';
  }
}
